import { getMainAPIClient } from '@/api/openapi'

// These utilities call the AI Visibility endpoints through the shared
// openapi-client-axios instance (see src/api/openapi.js). The client injects
// the auth token and the active subAccountRelationId via request interceptors
// and shares the 401 handling used across the app, so callers no longer pass
// headers/params manually. Each function returns the axios response, so
// existing `response.data...` usage in components keeps working unchanged.

export async function getSiteDetails (siteId) {
  return (await getMainAPIClient()).sites_single({ site_id: siteId })
}

export async function getAiVisibilityTrackers (siteId) {
  return (await getMainAPIClient()).ai_visibility_list_trackers({ site_id: siteId })
}

export async function getAdminAiVisibilityTrackers (params = {}) {
  return (await getMainAPIClient()).admin_ai_visibility_trackers_list(params)
}

export async function exportAdminAiVisibilityTrackersPage (params = {}) {
  return (await getMainAPIClient()).get(
    '/api/admin/ai-visibility-trackers/export-page/',
    {
      params,
      responseType: 'blob',
    },
  )
}

export async function exportAdminAiVisibilityTrackersAll (params = {}) {
  return (await getMainAPIClient()).get(
    '/api/admin/ai-visibility-trackers/export-all/',
    {
      params,
      responseType: 'blob',
    },
  )
}

export async function exportAdminAiVisibilityTrackerRunsPage (params = {}) {
  return (await getMainAPIClient()).get(
    '/api/admin/ai-visibility-trackers/runs/export-page/',
    {
      params,
      responseType: 'blob',
    },
  )
}

export async function exportAdminAiVisibilityTrackerRunsAll (params = {}) {
  return (await getMainAPIClient()).get(
    '/api/admin/ai-visibility-trackers/runs/export-all/',
    {
      params,
      responseType: 'blob',
    },
  )
}

export async function getAiVisibilityDetail (siteId, trackerId) {
  return (await getMainAPIClient()).ai_visibility_get_tracker({
    site_id: siteId,
    tracker_id: trackerId,
  })
}

export async function updateAiVisibilityTracker (siteId, trackerId, payload) {
  return (await getMainAPIClient()).ai_visibility_update_tracker(
    { site_id: siteId, tracker_id: trackerId },
    payload,
  )
}

export async function updateAiVisibilityTrackerRefreshCadence (
  siteId,
  trackerId,
  refreshFrequency,
) {
  return (await getMainAPIClient()).ai_visibility_update_tracker_refresh_cadence(
    { site_id: siteId, tracker_id: trackerId },
    { refreshFrequency },
  )
}

export async function pauseAiVisibilityTracker (siteId, trackerId) {
  return (await getMainAPIClient()).ai_visibility_pause_tracker(
    { site_id: siteId, tracker_id: trackerId },
    {},
  )
}

export async function resumeAiVisibilityTracker (siteId, trackerId) {
  return (await getMainAPIClient()).ai_visibility_resume_tracker(
    { site_id: siteId, tracker_id: trackerId },
    {},
  )
}

export async function deleteAiVisibilityTracker (siteId, trackerId) {
  return (await getMainAPIClient()).ai_visibility_delete_tracker({
    site_id: siteId,
    tracker_id: trackerId,
  })
}

export async function getAiVisibilityProgress (siteId, trackerId) {
  return (await getMainAPIClient()).ai_visibility_tracker_progress({
    site_id: siteId,
    tracker_id: trackerId,
  })
}

export async function getAiVisibilityOverview (siteId, trackerId, filters = {}) {
  return (await getMainAPIClient()).ai_visibility_tracker_overview({
    site_id: siteId,
    tracker_id: trackerId,
    ...filters,
  })
}

export async function getAiVisibilityCitations (siteId, trackerId, filters = {}) {
  return (await getMainAPIClient()).ai_visibility_tracker_citations({
    site_id: siteId,
    tracker_id: trackerId,
    ...filters,
  })
}

export async function getAiVisibilityCompetitors (siteId, trackerId, filters = {}) {
  return (await getMainAPIClient()).ai_visibility_tracker_competitors({
    site_id: siteId,
    tracker_id: trackerId,
    ...filters,
  })
}

export async function getAiVisibilityQuestions (siteId, trackerId, filters = {}) {
  return (await getMainAPIClient()).ai_visibility_tracker_questions({
    site_id: siteId,
    tracker_id: trackerId,
    ...filters,
  })
}

export async function getAiVisibilityQuestionSettings (siteId, trackerId) {
  return (await getMainAPIClient()).ai_visibility_get_question_settings({
    site_id: siteId,
    tracker_id: trackerId,
  })
}

export async function createNewTrackerWithQuestions (siteId, trackerId, payload) {
  return (await getMainAPIClient()).ai_visibility_create_new_tracker_with_questions(
    { site_id: siteId, tracker_id: trackerId },
    payload,
  )
}

export async function createAiVisibilityRun (siteId, trackerId) {
  return (await getMainAPIClient()).ai_visibility_create_run(
    { site_id: siteId, tracker_id: trackerId },
    {},
  )
}

export async function getCategorySuggestions ({ brandName, websiteUrl, locationCode, languageName }) {
  return (await getMainAPIClient()).ai_visibility_category_suggestions(
    null,
    { brandName, websiteUrl, locationCode, languageName },
  )
}

export async function getQuestionSuggestions ({
  brandName,
  websiteUrl,
  categories,
  market,
  locationCode,
  languageCode,
  languageName,
}) {
  return (await getMainAPIClient()).ai_visibility_question_suggestions(
    null,
    {
      brandName,
      websiteUrl,
      categories,
      market,
      locationCode,
      languageCode,
      languageName,
    },
  )
}

export async function createAiVisibilityTracker (setupDraft) {
  return (await getMainAPIClient()).ai_visibility_create_tracker(null, setupDraft)
}
